import {
  keepPreviousData,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  createOrganization,
  getOrganizationsList,
  getOrganization,
  updateOrganization,
} from './organizations.service';
import { GetOrganizationsParams, Organization } from './organizations.types';

// Query keys factory
export const organizationsQueryKeys = {
  all: ['organizations'] as const,
  list: (params: GetOrganizationsParams) => [
    ...organizationsQueryKeys.all,
    'list',
    params.page,
    params.perPage,
    params.sort,
    params.order,
    params.filter,
  ],
  organization: (id: string) =>
    [...organizationsQueryKeys.all, 'detail', id] as const,
};

// Get organizations list query
const getOrganizationsListQuery = (params: GetOrganizationsParams) => {
  const { ...rest } = params;
  const queryKey = organizationsQueryKeys.list(rest);

  return queryOptions({
    queryKey,
    queryFn: () => getOrganizationsList(params),
    staleTime: 0,
    refetchOnWindowFocus: false,
    gcTime: 0,
    placeholderData: keepPreviousData,
  });
};

// Hook to get organizations list
export const useGetOrganizations = (params: GetOrganizationsParams) => {
  return useQuery({
    ...getOrganizationsListQuery(params),
  });
};

// Hook to get single organization
export const useGetOrganization = (id: string | null | undefined) => {
  return useQuery({
    queryKey: organizationsQueryKeys.organization(id || ''),
    queryFn: () => getOrganization(id!),
    enabled: !!id,
    staleTime: 30 * 1000, // 30 seconds
    refetchOnWindowFocus: false,
    gcTime: 5 * 60 * 1000, // 5 minutes
    select: (data) => data.data,
  });
};

// Create organization mutation
export const useCreateOrganization = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createOrganization,
    onSuccess: () => {
      // Invalidate and refetch organizations list
      queryClient.invalidateQueries({
        queryKey: organizationsQueryKeys.all,
      });
    },
    onError: (error) => {
      console.error('Error creating organization:', error);
    },
  });
};

// Update organization mutation
export const useUpdateOrganization = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Organization> }) =>
      updateOrganization(id, data),
    onSuccess: (data, variables) => {
      // Update the specific organization in cache
      queryClient.setQueryData(
        organizationsQueryKeys.organization(variables.id),
        data,
      );

      // Invalidate organizations list to refresh
      queryClient.invalidateQueries({
        queryKey: organizationsQueryKeys.all,
      });
    },
    onError: (error) => {
      console.error('Error updating organization:', error);
    },
  });
};
